import './index.less';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    checkPointInStage,
    getBehavior,
    getFramework,
    getLayerId,
    getOperateType,
    getPointRect,
    getStageIndexByMousePointY,
    IFramework,
    IHoverExtendArgs,
    useFramework,
    useIdentity,
    useIsBasicOperate,
    useIsDisableOperate,
    useIsDisablePick,
    useLayerTranslate,
    useScale,
} from '@/framework';
import { isLeftButton } from '@/utils/event-kit';
import { getLayerCenterPoint, getLayerInfoWithRotate } from '@/utils/layer-kit';
import {
    IExportPlugin,
    ILayerProps,
    PluginType,
    REFERENCE_LINE_EVENT_NAMES,
    SMART_GUIDE_EVENT_NAMES,
} from '@/version-types';
import { useGroupPickState } from '../states/group-inner-state';
import mouseOperateState, { useMouseOperateState } from '../states/mouse-operate-state';
import { ImageOperate } from './operates/image-operate/image-operate';
import { LockOperate } from './operates/lock-operate';
import { MoveOperate } from './operates/move-operate';
import { ResizeOperate } from './operates/resize-operate';
import { RotateOperate } from './operates/rotate-operate';
import { ScaleOperate } from './operates/scale-operate';

const ChildrenBorder: React.FC<{ layer: ILayerProps }> = React.memo(({ layer }) => {
    return (
        <>
            {(layer.children || []).map((item) => {
                return (
                    <BaseOperateView key={item.id} layer={item}>
                        <div className="active-operate"></div>
                    </BaseOperateView>
                );
            })}
        </>
    );
});

/**
 * 获取禁用选中图层基础操作功能的操作框
 */
const getGroupExtensionNotPickLayerOperate = (
    framework: IFramework,
    refs: {
        layerNodeRef: React.RefObject<HTMLDivElement>;
        pickLayerNodeRef: React.RefObject<HTMLDivElement>;
        refNode: React.RefObject<HTMLDivElement>;
        childrenNodeRef: React.RefObject<HTMLDivElement>;
    },
    activeData: { layer: ILayerProps | null; pickLayer: ILayerProps | null },
) => {
    if (!activeData.layer || !framework.checkIsGroupExtension(activeData.layer)) return null;
    const operateLayer = activeData.pickLayer || activeData.layer;
    if (!operateLayer || !framework.checkDisableChildBaseOperate(operateLayer)) return null;
    const customActiveOperate =
        framework.getBehavior(activeData.layer)?.customActiveOperate?.(activeData.layer) || null;
    const isLock = activeData.layer.lock;
    const useDefaultScaleOperate = framework.getBehavior(activeData.layer)?.useDefaultScaleOperate?.(activeData.layer);
    const useDefaultResizeOperate = framework
        .getBehavior(activeData.layer)
        ?.useDefaultResizeOperate?.(activeData.layer);
    return (
        <>
            {activeData.pickLayer ? (
                <BaseOperateView layer={framework.getLayer(activeData.pickLayer.id)}>
                    <div
                        ref={refs.childrenNodeRef}
                        className={classNames('active-operate', {
                            'is-lock': isLock,
                        })}
                    ></div>
                </BaseOperateView>
            ) : null}
            <BaseOperateView ref={refs.refNode} layer={activeData.layer}>
                <div
                    ref={refs.layerNodeRef}
                    className={classNames('active-operate', 'is-group', {
                        'is-lock': isLock,
                    })}
                >
                    {isLock ? (
                        <LockOperate layerId={activeData.layer.id} />
                    ) : activeData.pickLayer?.__isCropImage || activeData.pickLayer?.__isDeformationImage ? null : (
                        <>
                            <MoveOperate layer={activeData.layer} />
                            <RotateOperate layer={activeData.layer} />
                            {customActiveOperate}
                            {useDefaultScaleOperate ? <ScaleOperate layer={activeData.layer} /> : null}
                            {useDefaultResizeOperate ? <ResizeOperate layer={activeData.layer} /> : null}
                        </>
                    )}
                </div>
            </BaseOperateView>
        </>
    );
};

const RenderActive: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const layerNodeRef = useRef<HTMLDivElement>(null);
    const pickLayerNodeRef = useRef<HTMLDivElement>(null);
    const childrenNodeRef = useRef<HTMLDivElement>(null);
    const [activeData, setActiveData] = useState<{
        layer: ILayerProps | null;
        pickLayer: ILayerProps | null;
    } | null>(null);
    const groupOperateIsActive = useGroupPickState()((state) => state.isActive);

    const translateLayerId = activeData?.pickLayer ? activeData?.pickLayer?.id : activeData?.layer?.id;
    const refNode = useLayerTranslate(
        framework.checkIsGroupExtension(activeData?.layer) ? activeData?.layer?.id : translateLayerId,
    );

    const setReferenceLines = useMouseOperateState()((state) => state.setReferenceLines);

    useEffect(() => {
        const handle = framework.events.onActiveLayerDataUpdate((layer, pickLayer) => {
            const activeLayer = framework.operate.getActiveLayer();
            setActiveData({ layer: activeLayer, pickLayer });
        });
        const baseHandle = framework.events.onBaseOperateChange((state) => {
            const className = 'is-basic-operate';
            const operate = state ? 'add' : 'remove';
            pickLayerNodeRef.current?.classList[operate](className);
            layerNodeRef.current?.classList[operate](className);
            if (childrenNodeRef.current) {
                childrenNodeRef.current.style.visibility = state ? 'hidden' : 'visible';
            }
        });
        return () => {
            framework.events.off(handle);
            framework.events.off(baseHandle);
        };
    }, []);

    useEffect(() => {
        // 接收参考线数据, 并保存到 store 中
        const handler = framework.events.on(REFERENCE_LINE_EVENT_NAMES.SYNC, (lines) => {
            setReferenceLines(lines);
        });

        // 激活图层初始化比参考线晚, 所以需要主动发起参考线同步请求
        framework.events.emit(REFERENCE_LINE_EVENT_NAMES.REQUEST_SYNC);
        return () => framework.events.off(handler);
    }, []);

    useEffect(() => {
        const downHandle = framework.events.onMouseDown((ev) => {
            if (!isLeftButton(ev)) return;

            const target = ev.target as HTMLElement;
            let id = getLayerId(identity, target);

            const operateType = getOperateType(target);
            if (operateType) return;

            console.log('id', id);

            if (id) {
                const hoverExtend = framework.operate.hoverExtend.get();
                if (hoverExtend) {
                    const layer = framework.getLayer(id);
                    if (layer.children?.length) {
                        const mouseDownRect = getPointRect(identity, {
                            x: ev.pageX,
                            y: ev.pageY,
                        });
                        id = framework.stage.tryPickChild(mouseDownRect, layer.children) || id;
                    }
                    framework.operate.hoverExtend.apply(id);
                }
                framework.operate.setActiveLayer(id);
                framework.operate.delHoverId();
                framework.stage.setActiveStageIndexByLayerId(id);
                framework.stage.setIsPickStage(false);
            } else {
                framework.operate.delActiveLayer();

                const mPoint = { x: ev.pageX, y: ev.pageY };
                const isLongImageMode = framework.stage.isLongImageMode();
                const stageIndex = isLongImageMode
                    ? getStageIndexByMousePointY(identity, mPoint.y)
                    : framework.stage.getActiveStageIndex();
                const isInStage = checkPointInStage(identity, mPoint, stageIndex);
                if (isInStage) {
                    framework.stage.setActiveStageIndex(stageIndex);
                }
                framework.stage.setIsPickStage(isInStage);
            }
        });
        const upHandle = framework.events.onMouseUp(() => {
            // const target = ev.target as HTMLElement;
            // const id = target.getAttribute('data-layer-id');
        });
        return () => {
            framework.events.off(downHandle);
            framework.events.off(upHandle);
        };
    }, []);

    useEffect(() => {
        // 激活图层变更，如果正在进行基础操作，重置基础操作状态
        return () => {
            if (framework.operate.isBaseOperate()) {
                framework.operate.setIsBaseOperate(false);
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_BLOCKS, []);
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_LINES, []);
                framework.events.emit(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, []);
            }
        };
    }, [activeData?.layer?.id]);

    // 监听hover扩展数据
    const [hoverExtend, setHoverExtend] = useState<IHoverExtendArgs | null>(framework.operate.hoverExtend.get());
    useEffect(() => {
        const handler = framework.operate.hoverExtend.onChange((data) => {
            setHoverExtend(data);
        });
        return () => {
            framework.events.off(handler);
        };
    }, []);

    if (!activeData?.layer || groupOperateIsActive) return null;

    const isExtraGroup = framework.checkIsExtraGroup(activeData.layer);
    const isGroup = framework.checkIsGroup(activeData.layer);
    const operateLayer = activeData.pickLayer || activeData.layer;
    const behavior = getBehavior(identity, operateLayer);
    const customActiveOperateElement = behavior?.customActiveOperate?.(operateLayer) || null;
    const isLock = framework.checkIsNeedLock(operateLayer.id);
    const isHidden = framework.checkIsNeedHidden(operateLayer.id);
    const hasPickLayer = !!activeData.pickLayer;
    const enableStretch = !behavior?.checkDisableStretch?.(operateLayer);
    const cursorStyle: React.CSSProperties = hoverExtend
        ? { cursor: `var(${hoverExtend.cursor})`, pointerEvents: 'all' }
        : {};
    // 需要选中组外壳
    const cursorDataLayerId = hoverExtend
        ? {
              'data-layer-id': activeData.layer.id,
          }
        : {};

    const groupExtensionNotPickLayerOperate = getGroupExtensionNotPickLayerOperate(
        framework,
        {
            layerNodeRef,
            pickLayerNodeRef,
            refNode,
            childrenNodeRef,
        },
        activeData,
    );
    const disablePickLayerOperate = framework.checkDisableChildBaseOperate(activeData.pickLayer);

    // console.log('groupExtensionNotPickLayerOperate', groupExtensionNotPickLayerOperate);

    return (
        <>
            {/* 快捷操作 */}
            {isLock || isHidden ? null : <ShortcutOperateContainer layer={activeData.layer} />}
            <BaseStagePlugin originIndexOrLayerId={activeData.layer.id} name="active-layer">
                {groupExtensionNotPickLayerOperate ? (
                    groupExtensionNotPickLayerOperate
                ) : (
                    <>
                        {hasPickLayer ? (
                            <BaseOperateView
                                layer={activeData.layer}
                                style={{
                                    zIndex: isLock ? 2 : 'auto',
                                }}
                            >
                                <div
                                    ref={pickLayerNodeRef}
                                    className={classNames('active-operate', 'is-group', {
                                        'is-lock': isLock,
                                    })}
                                    style={cursorStyle}
                                    {...cursorDataLayerId}
                                >
                                    {isLock ? <LockOperate layerId={activeData.layer.id} /> : null}
                                </div>
                            </BaseOperateView>
                        ) : (
                            <>
                                {isExtraGroup ? (
                                    <div ref={childrenNodeRef}>
                                        <ChildrenBorder layer={activeData.layer} />
                                    </div>
                                ) : null}
                            </>
                        )}
                        <BaseOperateView ref={refNode} layer={operateLayer}>
                            <div
                                ref={layerNodeRef}
                                className={classNames(!operateLayer.__isDeformationImage ? 'active-operate' : '', {
                                    'is-group': !hasPickLayer && isGroup,
                                    'is-lock': isLock,
                                })}
                            >
                                {disablePickLayerOperate ? null : isLock ? (
                                    <>{hasPickLayer ? null : <LockOperate layerId={activeData.layer.id} />}</>
                                ) : operateLayer.__isCropImage || operateLayer.__isDeformationImage ? null : (
                                    <>
                                        <MoveOperate layer={operateLayer} />
                                        <RotateOperate layer={operateLayer} />
                                        {customActiveOperateElement ? (
                                            customActiveOperateElement
                                        ) : enableStretch ? (
                                            <>
                                                <ScaleOperate layer={operateLayer} />
                                                <ResizeOperate layer={operateLayer} />
                                            </>
                                        ) : null}

                                        <ImageOperate framework={framework} activeData={activeData} />
                                    </>
                                )}
                            </div>
                        </BaseOperateView>
                    </>
                )}
            </BaseStagePlugin>
        </>
    );
});

/**
 * 快捷操容器
 */
const ShortcutOperateContainer: React.FC<{ layer: ILayerProps }> = React.memo(({ layer }) => {
    const scale = useScale();
    const isBaseOperate = useIsBasicOperate();
    const disablePick = useIsDisablePick();
    const disableOperate = useIsDisableOperate();
    const nodeRef = useRef<HTMLDivElement>(null);
    const framework = useFramework();
    const [shortcutMenu, setShortcutMenu] = useState<React.ReactNode | null>();

    // 监听快捷操作菜单
    useEffect(() => {
        setShortcutMenu(framework.operate.shortcutMenu.get());
        const handler = framework.operate.shortcutMenu.onMenu((data) => {
            setShortcutMenu(data);
        });
        return () => framework.events.off(handler);
    }, []);

    useEffect(() => {
        const node = nodeRef.current;
        if (!node || !layer || !shortcutMenu) return;
        node.style.opacity = '0';
        const info = getLayerInfoWithRotate(layer);
        const { x } = getLayerCenterPoint(layer);
        let offset = layer.rotate > 170 && layer.rotate < 190 ? 26 : 0;
        let y = info.top * scale - 45 - offset;
        y -= framework.getBehavior(layer)?.shortcutMenuOffsetY?.(layer) || 0;
        node.style.left = `${x * scale}px`;
        node.style.top = `${y}px`;
        if (y < 0) {
            // 如果快捷操作框超出顶部，则将快捷操作框移动到图层下方
            const rect = node.getBoundingClientRect();
            if (rect.top < 54) {
                offset = (layer.rotate <= 10 && layer.rotate >= 0) || layer.rotate >= 350 ? 26 : 0;
                // console.log(layer.rotate, offset);
                y = (info.top + info.height) * scale + 10 + offset;
                node.style.top = `${y}px`;
            }
        }
        node.style.opacity = '1';
    }, [layer, scale, shortcutMenu]); // 需要监听 !!shortcutMenu 的变化, 否则表格在通用shortcut和自定义shortcut中切换时位置不对

    const renderNode = !(isBaseOperate || disablePick || disableOperate || !shortcutMenu);

    return (
        <BaseStagePlugin originIndexOrLayerId={layer.id} style={{ zIndex: 19 }} name="shortcut-operate-tip">
            <div
                ref={nodeRef}
                className={classNames('shortcut-operate-container', {
                    'is-hidden': !renderNode,
                })}
            >
                {renderNode ? shortcutMenu : null}
            </div>
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 2,
    name: 'active-layer',
    render() {
        return <RenderActive />;
    },
    createState() {
        return [mouseOperateState];
    },
};

export default exportPlugin;
